import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Modal, ModalBody } from "reactstrap";
import {
    colors,
    tableCardStyle,
    emptyStateStyle,
    emptyStateSuccessStyle,
    listContainerStyle,
    getRowCardStyle,
    rowCardHoverStyle,
    rowCardLeftStyle,
    rowCardNameStyle,
    rowCardMacStyle,
    rowCardChipsStyle,
    rowChevronStyle,
    modalHeaderStyle,
    modalTitleStyle,
    modalCloseButtonStyle,
    modalBodyWrapStyle,
    modalSectionTitleStyle,
    modalChipsRowStyle,
    serverBadgeStyle,
    clientIdBadgeStyle,
    clientIdRowStyle,
    createdOnListStyle,
    getCreatedOnBadgeStyle,
    createdOnLabelStyle,
    createdOnIconStyle,
    getVersionCardStyle,
    versionLabelChipStyle,
    versionDetailsStyle,
    versionValueStyle,
    versionOkStyle,
    updateRequiredStyle,
    invoiceMismatchCardStyle,
    deleteMismatchCardStyle,
    invoiceDetailsStyle,
    invoiceNumberStyle,
    invoiceMismatchMessageStyle,
    deleteMismatchMessageStyle,
    getStatusBadgeStyle,
    getStatusBadgeDetailedStyle,
    statusBadgeTopRowStyle,
    statusBadgeDetailTextStyle,
    getStatusIconClass,
    getStatusIconStyle,
    getStatusTone,
    getRowStatusIconWrapStyle,
    filterFieldLabelStyle,
    statusSelectStyles,
    // ---- summary/tab cards — reused exactly as-is from POSDashBoard.js ----
    summaryStripStyle,
    getSummaryCardStyle,
    getSummaryCardAccentStyle,
    summaryCardHoverStyle,
    getSummaryIconChipStyle,
    summaryCardTextWrapStyle,
    summaryCardLabelStyle,
    summaryCardDescStyle,
    getSummaryCardValueStyle,
} from "./posDashboard.styles";
import {
    applyAttentionSubTab,
    applyStatusFilters,
    applyVersionFilter,
    attentionSubTabOptions,
    buildSearchText,
    formatAgo,
    formatDateTimeShort,
    formatOfflineDuration,
    getSubTabChips,
    getUniqueVersionOptions,
    getVisibleBadges,
    getVersionMismatchChips,
    isUpdateRequired,
    normalizeText,
    normalizeInvoiceNo,
    getBillTimeMismatchFlags,
    getServiceNotWorkingGapMinutes,
    getBillTimeMismatchMaxGapMinutes,
    getMaxIssueGapMinutes,
    getDeleteChip,
    getInvoiceChip,
    getAttentionSubTabCounts,
    isBillTimeOnlyRow,
} from "./posDashboard.logic";

/* ============================================================
   Local-only bits (search box / ghost button) that
   posDashboard.styles.js doesn't define, kept on the same
   `colors` tokens so they still feel like one page.
   ============================================================ */
const ghostButtonStyle = {
    fontSize: "12.5px",
    fontWeight: 600,
    color: colors.primary,
    background: colors.primaryLight,
    border: `1px solid ${colors.primaryBorder}`,
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
};

const searchWrapStyle = { position: "relative" };
const searchIconStyle = {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: colors.gray400,
    fontSize: "14px",
};
const searchInputStyle = {
    fontSize: "13px",
    padding: "8px 10px 8px 30px",
    borderRadius: "8px",
    border: `1px solid ${colors.gray200}`,
    outline: "none",
    width: "220px",
    background: colors.white,
};
const versionFilterWrapStyle = { minWidth: "200px" };

/* ============================================================
   SUMMARY CARDS — same 4 cards/order/values/definitions as
   POSDashBoard.js: Needs Attention -> Offline -> Running OK ->
   All Machines. Category membership is driven off IsOffline /
   IsOk exactly like the page (not off VersionMismatchList
   string-matching, which the page doesn't use either).
   ============================================================ */
const SUMMARY_CARDS = [
    {
        key: "attention",
        tone: "warning",
        icon: "bx-error-circle",
        label: "Needs Attention",
        desc: "Machines with an issue to check",
    },
    {
        key: "offline",
        tone: "danger",
        icon: "bx-wifi-off",
        label: "Offline",
        desc: "Not connected right now",
    },
    {
        key: "all",
        tone: "neutral",
        icon: "bx-devices",
        label: "All Machines",
        desc: "Everything, healthy or not",
    },
];

/* ============================================================
   MAIN COMPONENT
   rows            -> real API rows (post-saga), never mocked
   serviceRows     -> tableData.ServiceRows, the separate service-heartbeat
                      feed the page's "Service Not Working" sub-tab reads
                      from — same shape/source as POSDashBoard.js
   tableColumns    -> [{ dataField }], gates which modal sections show
   IsMannagementParty -> drives "show all machines" toggle
   partyNameMap    -> { [Party]: Name }
   isFranchise -> franchise users only see their own party, so the
                      party name is redundant and the ClientID leads instead
   ============================================================ */
const PosDashboardTable = ({
    rows = [],
    serviceRows = [],
    tableColumns = [],
    IsMannagementParty = true,
    partyNameMap = {},
    isFranchise = false,
}) => {
    const [selectedId, setSelectedId] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [showAllServers, setShowAllServers] = useState(false);
    // Same default view + same tab/sub-tab model as POSDashBoard.js
    const [activeTab, setActiveTab] = useState("attention");
    const [attentionSubTab, setAttentionSubTab] = useState("all");
    const [okSubTab, setOkSubTab] = useState("all");
    const [versionFilter, setVersionFilter] = useState([]);
    const [query, setQuery] = useState("");
    const showPartyName = !isFranchise;

    // Keep "Offline · Xm ago" / "X ago" live without a manual refresh.
    const [, forceTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => forceTick((n) => n + 1), 60000);
        return () => clearInterval(id);
    }, []);

    // This component only mounts once its parent already has real, fetched
    // rows (there's no separate Go-button fetch cycle here like the page),
    // so "hasSearched" is always true — kept as a named const purely so the
    // shared applyStatusFilters signature matches POSDashBoard.js exactly.
    const hasSearched = true;

    useEffect(() => {
        if (activeTab !== "attention") setAttentionSubTab("all");
        if (activeTab !== "ok") setOkSubTab("all");
    }, [activeTab]);

    const hasColumn = (dataField) => {
        if (!tableColumns || tableColumns.length === 0) return true;
        return tableColumns.some((c) => c.dataField === dataField);
    };

    const getPartyDisplayName = (row) => partyNameMap[row.Party] || `Party ${row.Party}`;

    const allVisibleRows = useMemo(() => rows || [], [rows]);

    // No stale-offline hiding — every row passed in is in scope.
    const serverRows = useMemo(
        () => allVisibleRows.filter((r) => Number(r.IsServerMachine) === 1),
        [allVisibleRows]
    );

    // Scope: which set of machines are currently in view (the LIST below the
    // cards). Server-only default + slice-of-6 unchanged.
    const scopedRows = showAll
        ? allVisibleRows
        : IsMannagementParty
            ? showAllServers ? serverRows : serverRows.slice(0, 6)
            : serverRows;

    // Version options + version-scoped full dataset — same pattern as
    // POSDashBoard.js's versionOptions / versionScopedData, computed off
    // the FULL dataset so the numbers on the cards stay independent of the
    // server-only / slice-of-6 / showAll scoping below.
    const versionOptions = useMemo(
        () => getUniqueVersionOptions(allVisibleRows),
        [allVisibleRows]
    );

    const versionScopedAll = useMemo(() => {
        if (!versionFilter || versionFilter.length === 0) return allVisibleRows;
        return allVisibleRows.filter((client) =>
            versionFilter.some((v) => applyVersionFilter(client, v))
        );
    }, [allVisibleRows, versionFilter]);

    const allRecordsOK = useMemo(() => {
        if (allVisibleRows.length === 0) return false;
        return allVisibleRows.every((r) => Number(r.IsOk) === 1);
    }, [allVisibleRows]);

    const attentionPoolData = useMemo(
        () => versionScopedAll.filter(
            (r) => Number(r.IsOffline) !== 1 && Number(r.IsOk) !== 1
        ),
        [versionScopedAll]
    );

    const billTimeOnlyCount = useMemo(
        () => attentionPoolData.filter(isBillTimeOnlyRow).length,
        [attentionPoolData]
    );
    // Needs Attention excludes time-only machines
    const needsAttentionTotal = attentionPoolData.length - billTimeOnlyCount;

    // ClientIDs in the leftover pool AND currently in view (respects the
    // server-only / slice-of-6 / showAll scoping) — keeps the Service Not
    // Working sub-tab's list consistent with the same view every other
    // sub-tab renders from, on top of the same Offline/OK exclusion.
    const attentionPoolClientIds = useMemo(
        () => new Set(attentionPoolData.map((r) => r.ClientID)),
        [attentionPoolData]
    );
    const scopedClientIds = useMemo(
        () => new Set(scopedRows.map((r) => r.ClientID)),
        [scopedRows]
    );

    // Service Not Working reads from the SEPARATE ServiceRows feed — same
    // source and merge pattern as POSDashBoard.js: each service row's own
    // POSDasboardSyncFrequency wins, falling back to the value on the
    // matching main-data row by ClientID when the service row doesn't have one.
    const serviceOnlyVisibleData = useMemo(() => {
        const syncFrequencyByClientId = new Map(
            allVisibleRows.map((row) => [row.ClientID, row.POSDasboardSyncFrequency])
        );

        return (serviceRows || []).map((row) => ({
            ...row,
            POSDasboardSyncFrequency:
                row.POSDasboardSyncFrequency ?? syncFrequencyByClientId.get(row.ClientID),
        }));
    }, [serviceRows, allVisibleRows]);

    // Service-only rows, version-filtered — mirrors the filtering the
    // "Service Not Working" sub-tab itself applies before its list renders.
    const serviceOnlyScopedData = useMemo(() => {
        if (!versionFilter || versionFilter.length === 0) return serviceOnlyVisibleData;
        return serviceOnlyVisibleData.filter((client) =>
            versionFilter.some((v) => applyVersionFilter(client, v))
        );
    }, [serviceOnlyVisibleData, versionFilter]);

    // Service-only rows, scoped down to machines actually in the attention
    // pool (not offline, not OK) — prevents Service Not Working from
    // showing machines that are already Offline or Running OK elsewhere.
    const serviceNotWorkingScopedData = useMemo(
        () => serviceOnlyScopedData.filter((r) => attentionPoolClientIds.has(r.ClientID)),
        [serviceOnlyScopedData, attentionPoolClientIds]
    );

    const attentionSubTabCounts = useMemo(
        () => getAttentionSubTabCounts(attentionPoolData, serviceNotWorkingScopedData),
        [attentionPoolData, serviceNotWorkingScopedData]
    );

    const okSubTabCounts = useMemo(() => ({
        all: versionScopedAll.filter((r) => Number(r.IsOk) === 1).length,
        server: versionScopedAll.filter(
            (r) => Number(r.IsOk) === 1 && Number(r.IsServerMachine) === 1
        ).length,
        nonServer: versionScopedAll.filter(
            (r) => Number(r.IsOk) === 1 && Number(r.IsServerMachine) !== 1
        ).length,
    }), [versionScopedAll]);

    // Card counts ALWAYS reflect the full (version-scoped) dataset — same as
    // POSDashBoard.js's summaryStats — independent of showAll / server-only /
    // slice-of-6, which only control which ROWS render in the list below.
    const summaryStats = useMemo(() => {
        const total = versionScopedAll.length;
        const offline = versionScopedAll.filter((r) => Number(r.IsOffline) === 1).length;
        const ok = versionScopedAll.filter((r) => Number(r.IsOk) === 1).length;
        return { total, offline, ok };
    }, [versionScopedAll]);

    const cardValueForKey = (key) => {
        if (key === "attention") return needsAttentionTotal;
        if (key === "offline") return summaryStats.offline;
        if (key === "ok") return summaryStats.ok;
        return versionScopedAll.length; // "all"
    };

    // ========== Row-card chips — identical logic to POSDashBoard.js's
    // getRowChips: only the issue(s) that actually apply to this machine,
    // collapsed to one chip per issue type. Full detail lives in the modal.
    const getRowChips = (row, isOkTab = false) => {
        const chips = [];
        const isOffline = Number(row.IsOffline) === 1;

        if (isOffline) {
            chips.push({
                text: row.LastSeenAt
                    ? `Offline · ${formatOfflineDuration(row.LastSeenAt)} ago`
                    : "Offline",
                tone: "danger",
            });
        }

        const invoiceChip = getInvoiceChip(row, isOkTab);
        if (invoiceChip) chips.push(invoiceChip);
        const deleteChip = getDeleteChip(row, isOkTab);
        if (deleteChip) chips.push(deleteChip);
        chips.push(...getVersionMismatchChips(row));

        if (chips.length === 0) {
            chips.push({ text: "All OK", tone: "success" });
        }

        return chips;
    };

    // ========== List filtering — same rule set as POSDashBoard.js's `rows`
    // memo. Every tab reads off `scopedRows` (this table's single merged
    // data source) EXCEPT Service Not Working, which — same as the page —
    // reads off the separate ServiceRows feed instead.
    const visibleRows = useMemo(() => {
        const isServiceSubTab = activeTab === "attention" && attentionSubTab === "serviceNotWorking";

        let clients = isServiceSubTab
            ? serviceOnlyVisibleData.filter(
                (r) => attentionPoolClientIds.has(r.ClientID) && scopedClientIds.has(r.ClientID)
            )
            : scopedRows;

        // versionScopedAll/serviceOnlyScopedData already apply the version
        // filter for cards/counts — this branch needs it applied separately
        // since the displayed list doesn't go through those.
        if (versionFilter && versionFilter.length > 0) {
            clients = clients.filter((client) =>
                versionFilter.some((v) => applyVersionFilter(client, v))
            );
        }

        if (activeTab === "ok") {
            clients = clients.filter((client) => {
                if (Number(client.IsOk) !== 1) return false;
                if (okSubTab === "server") return Number(client.IsServerMachine) === 1;
                if (okSubTab === "nonServer") return Number(client.IsServerMachine) !== 1;
                return true;
            });
        }

        // Service Not Working has its own self-contained rule (lastSync gap
        // only, checked below via applyAttentionSubTab) — skip the OK/offline
        // status filter here so a row can't be excluded for any reason other
        // than that gap, same as POSDashBoard.js.
        if (!isServiceSubTab) {
            clients = clients.filter((client) =>
                applyStatusFilters(client, hasSearched, query, allRecordsOK, activeTab)
            );
        }

        if (activeTab === "attention") {
            clients = clients.filter((client) => applyAttentionSubTab(client, attentionSubTab));

            // Worst-first ordering for time-based sub-tabs.
            if (attentionSubTab === "serviceNotWorking") {
                clients = [...clients].sort(
                    (a, b) => getServiceNotWorkingGapMinutes(b) - getServiceNotWorkingGapMinutes(a)
                );
            } else if (attentionSubTab === "billTimeWise") {
                clients = [...clients].sort(
                    (a, b) => getBillTimeMismatchMaxGapMinutes(b) - getBillTimeMismatchMaxGapMinutes(a)
                );
            } else if (attentionSubTab === "all") {
                clients = [...clients].sort(
                    (a, b) => getMaxIssueGapMinutes(b) - getMaxIssueGapMinutes(a)
                );
            }
        }

        // Offline tab — longest-offline-first.
        if (activeTab === "offline") {
            clients = [...clients].sort((a, b) => {
                const aGap = a.LastSeenAt ? Date.now() - new Date(a.LastSeenAt).getTime() : Infinity;
                const bGap = b.LastSeenAt ? Date.now() - new Date(b.LastSeenAt).getTime() : Infinity;
                return bGap - aGap;
            });
        }

        if (query) {
            const q = normalizeText(query.trim());
            clients = clients.filter((client) => {
                const baseMatch = buildSearchText(client, partyNameMap).includes(q);
                const versionMatch = (client.Versions || []).some((v) =>
                    normalizeText(`${v.label ?? ""} ${v.running ?? ""} ${v.current ?? ""}`).includes(q)
                );
                return baseMatch || versionMatch;
            });
        }

        return clients;
    }, [
        scopedRows, serviceOnlyVisibleData, attentionPoolClientIds, scopedClientIds,
        versionFilter, activeTab, attentionSubTab, okSubTab,
        query, allRecordsOK, partyNameMap, hasSearched,
    ]);

    const selectedRow = visibleRows.find((r) => r.ClientID === selectedId) || null;

    /* ---------- modal sections (same logic as POSDashBoard.js) ---------- */
    const renderCreatedOnSection = (row) => {
        const entries = [];
        if (row.PosLastSyncRaw) {
            entries.push({ isPos: true, display: formatAgo(row.PosLastSyncRaw), absolute: row.PosLastSync });
        } else if (row.PosLastSync) {
            entries.push({ isPos: true, display: row.PosLastSync, absolute: row.PosLastSync });
        }
        if (row.ServiceLastSyncRaw) {
            entries.push({ isPos: false, display: formatAgo(row.ServiceLastSyncRaw), absolute: row.ServiceLastSync });
        } else if (row.ServiceLastSync) {
            entries.push({ isPos: false, display: row.ServiceLastSync, absolute: row.ServiceLastSync });
        }
        if (entries.length === 0) return null;

        return (
            <div style={createdOnListStyle}>
                {entries.map((e, i) => (
                    <div key={i} style={getCreatedOnBadgeStyle(e.isPos)} title={e.absolute || ""}>
                        <span style={createdOnIconStyle}>
                            <i className={e.isPos ? "bx bx-time-five" : "bx bx-history"}></i>
                        </span>
                        <span style={createdOnLabelStyle}>{e.isPos ? "POS" : "Service"}:</span>
                        {e.display}
                    </div>
                ))}
            </div>
        );
    };

    const renderVersionsSection = (row) => {
        if (!row.Versions?.length) return null;
        return (
            <>
                {row.Versions.map((v, i) => {
                    const updateRequired = isUpdateRequired(v.running, v.current);
                    const isPos = v.label === "POS";

                    if (!updateRequired) {
                        return (
                            <div key={i} style={getVersionCardStyle(isPos)}>
                                <span style={versionLabelChipStyle(isPos)}>{v.label}</span>
                                <span style={versionOkStyle}>
                                    <i className="bx bx-check-circle"></i>
                                    OK
                                </span>
                                <span style={versionValueStyle}>{v.running}</span>
                            </div>
                        );
                    }

                    return (
                        <div key={i} style={getVersionCardStyle(isPos)}>
                            <span style={versionLabelChipStyle(isPos)}>{v.label}</span>
                            <span style={versionDetailsStyle}>
                                <span style={versionValueStyle}>{v.running}</span>
                                <i className="bx bx-right-arrow-alt"></i>
                                <span style={versionValueStyle}>{v.current}</span>
                            </span>
                            <span style={updateRequiredStyle}>
                                <i className="bx bx-up-arrow-circle"></i>
                                Update Required
                            </span>
                        </div>
                    );
                })}
            </>
        );
    };

    const renderInvoiceMismatchSection = (row) => {
        const isServerRow = Number(row.IsServerMachine) === 1;
        if (!isServerRow) return null;

        const numberMismatch =
            normalizeInvoiceNo(row.MaxInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPMaxInvoiceFullInvoiceNo);
        const { invoiceGapMismatch } = getBillTimeMismatchFlags(row);

        if (!numberMismatch && !invoiceGapMismatch) return null;

        return (
            <div style={invoiceMismatchCardStyle}>
                <div style={invoiceDetailsStyle}>
                    <div title={formatDateTimeShort(row.MaxInvoiceCreatedOn)}>
                        <strong>POS:</strong> <span>{formatAgo(row.MaxInvoiceCreatedOn)}</span>
                        {row.MaxInvoiceFullInvoiceNo && (
                            <span style={invoiceNumberStyle}>({row.MaxInvoiceFullInvoiceNo})</span>
                        )}
                    </div>
                    <div title={formatDateTimeShort(row.ERPMaxInvoiceCreatedOn)}>
                        <strong>ERP:</strong> <span>{formatAgo(row.ERPMaxInvoiceCreatedOn)}</span>
                        {row.ERPMaxInvoiceFullInvoiceNo && (
                            <span style={invoiceNumberStyle}>({row.ERPMaxInvoiceFullInvoiceNo})</span>
                        )}
                    </div>
                </div>
                <div style={invoiceMismatchMessageStyle}>
                    <i className="bx bx-error"></i>
                    {numberMismatch && invoiceGapMismatch ? "Invoice Number Mismatch & Not Synced" : numberMismatch ? "Invoice Number Mismatch" : "Invoice Not Synced"}
                </div>
            </div>
        );
    };

    const renderDeleteMismatchSection = (row) => {
        const isServerRow = Number(row.IsServerMachine) === 1;
        if (!isServerRow) return null;

        const numberMismatch =
            normalizeInvoiceNo(row.MaxDeletedInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPDeleteMaxInvoiceFullInvoiceNo);
        const { deleteGapMismatch } = getBillTimeMismatchFlags(row);

        if (!numberMismatch && !deleteGapMismatch) return null;

        return (
            <div style={deleteMismatchCardStyle}>
                <div style={invoiceDetailsStyle}>
                    <div title={formatDateTimeShort(row.MaxDeletedInvoiceCreatedOn)}>
                        <strong>POS:</strong> <span>{formatAgo(row.MaxDeletedInvoiceCreatedOn)}</span>
                        {row.MaxDeletedInvoiceFullInvoiceNo && (
                            <span style={invoiceNumberStyle}>({row.MaxDeletedInvoiceFullInvoiceNo})</span>
                        )}
                    </div>
                    <div title={formatDateTimeShort(row.ERPDeleteMaxInvoiceCreatedOn)}>
                        <strong>ERP:</strong> <span>{formatAgo(row.ERPDeleteMaxInvoiceCreatedOn)}</span>
                        {row.ERPDeleteMaxInvoiceFullInvoiceNo && (
                            <span style={invoiceNumberStyle}>({row.ERPDeleteMaxInvoiceFullInvoiceNo})</span>
                        )}
                    </div>
                </div>
                <div style={deleteMismatchMessageStyle}>
                    <i className="bx bx-error"></i>
                    {numberMismatch && deleteGapMismatch
                        ? "Delete Number Mismatch & Not Synced"
                        : numberMismatch
                            ? "Delete Number Mismatch"
                            : "Delete Not Synced"}
                </div>
            </div>
        );
    };

    // A sub-tab row should show only the reason that matched that sub-tab
    // (with its real numbers/times) — full detail stays in the modal.
    const useSubTabChips = activeTab === "attention" && attentionSubTab && attentionSubTab !== "all";
    const isOkTab = activeTab === "ok";

    return (
        <div style={{ padding: "18px" }}>
            {/* ---------- HEADER ---------- */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                    marginBottom: "14px",
                }}
            >
                <div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: colors.gray900 }}>
                        POS Machines
                    </div>
                    <div style={{ fontSize: "12.5px", color: colors.gray500 }}>
                        {scopedRows.length} machine{scopedRows.length === 1 ? "" : "s"} in view · click a card for details
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <div style={searchWrapStyle}>
                        <i className="bx bx-search" style={searchIconStyle}></i>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search machine, ID, MAC…"
                            style={searchInputStyle}
                        />
                    </div>

                    {IsMannagementParty && (
                        <button
                            onClick={() => {
                                setShowAll((v) => !v);
                                setSelectedId(null);
                            }}
                            style={ghostButtonStyle}
                        >
                            {showAll ? "Show servers only" : "Show all machines"}
                        </button>
                    )}
                </div>
            </div>

            {/* ---------- SUMMARY / VIEW-SWITCHER CARDS ----------
                Same 4 cards, same order, same values/definitions, and the
                exact same styles/hover behavior as POSDashBoard.js. */}
            {scopedRows.length > 0 && (
                <div style={summaryStripStyle}>
                    {SUMMARY_CARDS.map((card) => {
                        const isActive = activeTab === card.key;
                        const count = cardValueForKey(card.key);
                        return (
                            <div
                                key={card.key}
                                role="button"
                                tabIndex={0}
                                aria-pressed={isActive}
                                style={getSummaryCardStyle(card.tone, isActive)}
                                onClick={() => setActiveTab(card.key)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setActiveTab(card.key);
                                    }
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) Object.assign(e.currentTarget.style, summaryCardHoverStyle);
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) e.currentTarget.style.boxShadow = "0 2px 10px rgba(17,24,39,0.04)";
                                }}
                            >
                                {isActive && <div style={getSummaryCardAccentStyle(card.tone)}></div>}
                                <div style={getSummaryIconChipStyle(card.tone)}>
                                    <i className={`bx ${card.icon}`}></i>
                                </div>
                                <div style={summaryCardTextWrapStyle}>
                                    <div style={summaryCardLabelStyle}>
                                        {card.label}
                                        {isActive && <i className="bx bx-check-circle" style={{ color: colors.primary, fontSize: "14px" }}></i>}
                                    </div>
                                    {card.key === "attention" && billTimeOnlyCount > 0 ? (
                                        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                            <div>
                                                <div style={getSummaryCardValueStyle(card.tone)}>{needsAttentionTotal}</div>
                                                <div style={summaryCardDescStyle}>Issues to check</div>
                                            </div>

                                            <div style={{ fontSize: "20px", fontWeight: 700, color: "#475569", transform: "translateY(-8px)", lineHeight: 1 }}>+</div>

                                            <div
                                                role="button"
                                                title="Machines whose only problem is a bill time gap. Click to view."
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveTab("attention");
                                                    setAttentionSubTab("billTimeWise");
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div style={{ ...getSummaryCardValueStyle(card.tone), color: "#b45309" }}>{billTimeOnlyCount}</div>
                                                <div style={{ ...summaryCardDescStyle, color: "#b45309" }}>Bill time only</div>
                                            </div>

                                            <div style={{ fontSize: "20px", fontWeight: 700, color: "#475569", transform: "translateY(-8px)", lineHeight: 1 }}>=</div>

                                            <div>
                                                <div style={getSummaryCardValueStyle(card.tone)}>{needsAttentionTotal + billTimeOnlyCount}</div>
                                                <div style={summaryCardDescStyle}>Total</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={getSummaryCardValueStyle(card.tone)}>{count}</div>
                                            <div style={summaryCardDescStyle}>{card.desc}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ATTENTION SUB-TABS — only visible while "Needs Attention" is active */}
            {activeTab === "attention" && scopedRows.length > 0 && (() => {
                const regularOptions = attentionSubTabOptions.filter((o) => o.key !== "billTimeWise");
                const billTimeOption = attentionSubTabOptions.find((o) => o.key === "billTimeWise");

                const pillBase = {
                    borderRadius: "999px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: "6px", lineHeight: 1.2,
                    transition: "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                };

                const countBadge = (isActive, count, activeBg, idleBg, idleColor) => (
                    <span aria-hidden="true" style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: "18px", height: "18px", padding: "0 5px", borderRadius: "999px",
                        background: isActive ? activeBg : idleBg, color: isActive ? "#fff" : idleColor,
                        fontSize: "11px", fontWeight: 700, lineHeight: 1,
                    }}>{count}</span>
                );

                const clearIcon = (
                    <span aria-hidden="true" style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: "16px", height: "16px", borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)", fontSize: "14px", lineHeight: 1,
                    }}>&times;</span>
                );

                const billTimeActive = attentionSubTab === "billTimeWise";

                return (
                    <div style={{ margin: "0 0 14px" }}>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                            {regularOptions.map((opt) => {
                                const isActive = attentionSubTab === opt.key;
                                const count = attentionSubTabCounts[opt.key] || 0;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => setAttentionSubTab(isActive ? "all" : opt.key)}
                                        title={isActive ? "Click to clear" : opt.label}
                                        style={{
                                            ...pillBase,
                                            padding: isActive ? "6px 10px 6px 14px" : "6px 14px",
                                            border: `1px solid ${isActive ? colors.primary : colors.gray200}`,
                                            background: isActive ? colors.primary : "#fff",
                                            color: isActive ? "#fff" : (colors.gray700 || "#374151"),
                                            boxShadow: isActive ? "0 4px 10px rgba(81,86,190,0.18)" : "none",
                                        }}
                                    >
                                        {opt.label}
                                        {countBadge(isActive, count, "rgba(255,255,255,0.25)", colors.gray100 || "#f3f4f6", colors.gray700 || "#374151")}
                                        {isActive && clearIcon}
                                    </button>
                                );
                            })}

                            {billTimeOption && (
                                <>
                                    <span aria-hidden="true" style={{ width: "1px", height: "24px", background: colors.gray200, margin: "0 4px" }} />
                                    <button
                                        type="button"
                                        onClick={() => setAttentionSubTab(billTimeActive ? "all" : "billTimeWise")}
                                        title={billTimeActive ? "Click to clear" : "Not included in the Needs Attention total"}
                                        style={{
                                            ...pillBase,
                                            padding: billTimeActive ? "6px 10px 6px 12px" : "6px 12px",
                                            border: `1px ${billTimeActive ? "solid" : "dashed"} #d97706`,
                                            background: billTimeActive ? "#d97706" : "#fffbeb",
                                            color: billTimeActive ? "#fff" : "#b45309",
                                            boxShadow: billTimeActive ? "0 4px 10px rgba(217,119,6,0.22)" : "none",
                                        }}
                                    >
                                        <i className="bx bx-time-five" style={{ fontSize: "14px" }}></i>
                                        {billTimeOption.label}
                                        {countBadge(billTimeActive, attentionSubTabCounts.billTimeWise || 0, "rgba(255,255,255,0.25)", "#fde68a", "#92400e")}
                                        {billTimeActive && clearIcon}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* RUNNING OK SUB-TABS */}
            {activeTab === "ok" && scopedRows.length > 0 && (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "0 0 14px" }}>
                    {[
                        { key: "all", label: "All", count: okSubTabCounts.all },
                        { key: "server", label: "Server", count: okSubTabCounts.server },
                        { key: "nonServer", label: "Non Server", count: okSubTabCounts.nonServer },
                    ].map((opt) => {
                        const isActive = okSubTab === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => setOkSubTab(isActive ? "all" : opt.key)}
                                title={isActive ? "Click to clear" : opt.label}
                                style={{
                                    padding: isActive ? "6px 10px 6px 14px" : "6px 14px",
                                    borderRadius: "999px",
                                    border: `1px solid ${isActive ? colors.primary : colors.gray200}`,
                                    background: isActive ? colors.primary : "#fff",
                                    color: isActive ? "#fff" : (colors.gray700 || "#374151"),
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    lineHeight: 1.2,
                                    boxShadow: isActive ? "0 4px 10px rgba(81,86,190,0.18)" : "none",
                                    transition: "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                                }}
                            >
                                {opt.label}
                                <span
                                    aria-hidden="true"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: "18px",
                                        height: "18px",
                                        padding: "0 5px",
                                        borderRadius: "999px",
                                        background: isActive ? "rgba(255,255,255,0.25)" : (colors.gray100 || "#f3f4f6"),
                                        color: isActive ? "#fff" : (colors.gray700 || "#374151"),
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    {opt.count}
                                </span>
                                {isActive && (
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "16px",
                                            height: "16px",
                                            borderRadius: "50%",
                                            background: "rgba(255,255,255,0.2)",
                                            fontSize: "14px",
                                            lineHeight: 1,
                                        }}
                                    >
                                        &times;
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ---------- MACHINE LIST ---------- */}
            <div style={tableCardStyle}>
                {visibleRows.length > 0 && (
                    <div style={listContainerStyle}>
                        {visibleRows.map((row) => {
                            const chips = useSubTabChips ? getSubTabChips(row, attentionSubTab) : getRowChips(row, isOkTab);
                            return (
                                <div
                                    key={row.ClientID}
                                    style={getRowCardStyle(Number(row.IsServerMachine) === 1)}
                                    onClick={() => setSelectedId(row.ClientID)}
                                    onMouseEnter={(e) => Object.assign(e.currentTarget.style, rowCardHoverStyle)}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "none";
                                        e.currentTarget.style.borderColor = colors.gray200;
                                    }}
                                >
                                    <div style={rowCardLeftStyle}>
                                        <span style={getRowStatusIconWrapStyle((chips[0] || {}).tone)}>
                                            <i className={`bx ${getStatusIconClass((chips[0] || {}).text, (chips[0] || {}).tone)}`}></i>
                                        </span>
                                        <div>
                                            <div style={rowCardNameStyle}>
                                                {showPartyName ? getPartyDisplayName(row) : `Machine #${row.ClientID}`}
                                                {Number(row.IsServerMachine) === 1 && (
                                                    <span style={serverBadgeStyle}>SERVER</span>
                                                )}
                                            </div>
                                            {row.MacAddress && <div style={rowCardMacStyle}>{row.MacAddress}</div>}
                                            {showPartyName &&
                                                <div style={clientIdRowStyle}>
                                                    <span style={clientIdBadgeStyle}>
                                                        <i className="bx bx-hash" style={{ fontSize: "10px" }}></i>
                                                        ID: {row.ClientID}
                                                    </span>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div style={rowCardChipsStyle}>
                                        {chips.map((c, i) => (
                                            c.detail ? (
                                                <span key={i} style={getStatusBadgeDetailedStyle(c.tone)} title={c.title || undefined}>
                                                    <span style={statusBadgeTopRowStyle}>
                                                        <i className={`bx ${getStatusIconClass(c.text, c.tone)}`} style={getStatusIconStyle(c.tone)}></i>
                                                        {c.text}
                                                    </span>
                                                    <span style={statusBadgeDetailTextStyle}>{c.detail}</span>
                                                </span>
                                            ) : (
                                                <span key={i} style={getStatusBadgeStyle(c.tone)} title={c.title || undefined}>
                                                    <i className={`bx ${getStatusIconClass(c.text, c.tone)}`} style={getStatusIconStyle(c.tone)}></i>
                                                    {c.text}
                                                </span>
                                            )
                                        ))}
                                    </div>

                                    <i className="bx bx-chevron-right" style={rowChevronStyle}></i>
                                </div>
                            );
                        })}
                    </div>
                )}

                {visibleRows.length === 0 && (() => {
                    const isUnfiltered = !query;
                    const isGoodNews =
                        isUnfiltered && scopedRows.length > 0 && (activeTab === "attention" || activeTab === "offline");

                    if (isGoodNews) {
                        return (
                            <div style={emptyStateSuccessStyle}>
                                <i className="bx bx-check-shield" style={{ fontSize: "28px", display: "block", marginBottom: "8px", color: colors.success }}></i>
                                {activeTab === "attention"
                                    ? "All machines are running fine — nothing needs attention"
                                    : "No machines are offline right now"}
                            </div>
                        );
                    }

                    return (
                        <div style={emptyStateStyle}>
                            <i className="bx bx-inbox" style={{ fontSize: "28px", display: "block", marginBottom: "8px" }}></i>
                            {isUnfiltered && activeTab === "ok"
                                ? "No machines are running OK right now"
                                : "No machines match this view"}
                        </div>
                    );
                })()}

                {IsMannagementParty && !showAll && serverRows.length > 6 && !showAllServers && (
                    <div style={{ textAlign: "center", padding: "14px" }}>
                        <button onClick={() => setShowAllServers(true)} style={ghostButtonStyle}>
                            Show {serverRows.length - 6} more server{serverRows.length - 6 === 1 ? "" : "s"}
                        </button>
                    </div>
                )}
            </div>

            {/* ---------- DETAIL MODAL — same sections as POSDashBoard.js ---------- */}
            <Modal isOpen={!!selectedRow} toggle={() => setSelectedId(null)} centered scrollable>
                {selectedRow && (
                    <>
                        <div style={modalHeaderStyle}>
                            <div>
                                <div style={modalTitleStyle}>
                                    {showPartyName ? getPartyDisplayName(selectedRow) : `Machine #${selectedRow.ClientID}`}
                                    {Number(selectedRow.IsServerMachine) === 1 && (
                                        <span style={serverBadgeStyle}>SERVER</span>
                                    )}
                                </div>
                                {selectedRow.MacAddress && <div style={rowCardMacStyle}>{selectedRow.MacAddress}</div>}
                            </div>
                            <button
                                type="button"
                                style={modalCloseButtonStyle}
                                onClick={() => setSelectedId(null)}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>

                        <ModalBody style={{ padding: 0 }}>
                            <div style={modalBodyWrapStyle}>
                                <div>
                                    <div style={modalSectionTitleStyle}>Status</div>
                                    <div style={modalChipsRowStyle}>
                                        {getRowChips(selectedRow).map((c, i) => (
                                            c.detail ? (
                                                <span key={i} style={getStatusBadgeDetailedStyle(c.tone)} title={c.title || undefined}>
                                                    <span style={statusBadgeTopRowStyle}>
                                                        <i className={`bx ${getStatusIconClass(c.text, c.tone)}`} style={getStatusIconStyle(c.tone)}></i>
                                                        {c.text}
                                                    </span>
                                                    <span style={statusBadgeDetailTextStyle}>{c.detail}</span>
                                                </span>
                                            ) : (
                                                <span key={i} style={getStatusBadgeStyle(c.tone)}>
                                                    <i className={`bx ${getStatusIconClass(c.text, c.tone)}`} style={getStatusIconStyle(c.tone)}></i>
                                                    {c.text}
                                                </span>
                                            )
                                        ))}
                                    </div>
                                </div>

                                {hasColumn("CreatedOnList") && renderCreatedOnSection(selectedRow) && (
                                    <div>
                                        <div style={modalSectionTitleStyle}>Last Synced</div>
                                        {renderCreatedOnSection(selectedRow)}
                                    </div>
                                )}

                                {hasColumn("Versions") && renderVersionsSection(selectedRow) && (
                                    <div>
                                        <div style={modalSectionTitleStyle}>Versions</div>
                                        {renderVersionsSection(selectedRow)}
                                    </div>
                                )}

                                {hasColumn("InvoiceCreatedOn") && renderInvoiceMismatchSection(selectedRow) && (
                                    <div>
                                        <div style={modalSectionTitleStyle}>Invoice Created</div>
                                        {renderInvoiceMismatchSection(selectedRow)}
                                    </div>
                                )}

                                {hasColumn("DeletedInvoiceCreatedOn") && renderDeleteMismatchSection(selectedRow) && (
                                    <div>
                                        <div style={modalSectionTitleStyle}>Invoice Deleted</div>
                                        {renderDeleteMismatchSection(selectedRow)}
                                    </div>
                                )}

                                {getVisibleBadges(selectedRow.VersionMismatchList).length > 0 && (
                                    <div>
                                        <div style={modalSectionTitleStyle}>Additional Flags</div>
                                        <div style={modalChipsRowStyle}>
                                            {getVisibleBadges(selectedRow.VersionMismatchList).map((s, i) => (
                                                <span key={i} style={getStatusBadgeStyle(getStatusTone(s))}>
                                                    <i className={`bx ${getStatusIconClass(s, getStatusTone(s))}`} style={getStatusIconStyle(getStatusTone(s))}></i>
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ModalBody>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PosDashboardTable;